import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Stack,
  Typography,
  Chip,
  Paper,
  TableContainer,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete, Payment } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import * as paymentMethodService from '../services/paymentMethodService';
import { useTranslation } from 'react-i18next';
import {
  addPaymentMethod as addPaymentMethodToStore,
  updatePaymentMethod as updatePaymentMethodInStore,
  removePaymentMethod as removePaymentMethodFromStore,
} from '../../../app/store/slices/paymentMethodSlice';

const PaymentMethod = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editMethod, setEditMethod] = useState(null);
  const [methodName, setMethodName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const response = await paymentMethodService.getAllPaymentMethods();
      const data = response?.payload || response?.data || response || [];
      setPaymentMethods(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast.error(t('failed_to_load_payment_methods') || 'Failed to load payment methods');
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const handleSave = async () => {
    // Validation
    if (!methodName || methodName.trim() === '') {
      setError(t('payment_method_name_required') || 'Payment method name is required');
      return;
    }

    if (methodName.length > 100) {
      setError(t('name_too_long') || 'Name cannot exceed 100 characters');
      return;
    }

    if (description && description.length > 500) {
      setError(t('description_too_long') || 'Description cannot exceed 500 characters');
      return;
    }

    try {
      const descriptionValue = description.trim() || null;

      if (editMethod) {
        // Send complete DTO with Id, Name, and Description for update
        const payload = {
          Id: editMethod.Id,
          Name: methodName.trim(),
          Description: descriptionValue
        };
        const response = await paymentMethodService.updatePaymentMethod(editMethod.Id, payload);
        
        // Update Redux store
        dispatch(updatePaymentMethodInStore({
          id: editMethod.Id,
          value: methodName.trim().toUpperCase().replace(/\s+/g, '_'),
          label: methodName.trim(),
          name: methodName.trim(),
          isActive: editMethod.IsActive
        }));
        
        toast.success(t('payment_method_updated') || 'Payment method updated successfully');
      } else {
        // For create, send Name and Description (Id will be auto-generated)
        const payload = {
          Name: methodName.trim(),
          Description: descriptionValue
        };
        const response = await paymentMethodService.createPaymentMethod(payload);
        const newMethod = response?.payload || response?.data || response;
        
        // Add to Redux store
        if (newMethod && newMethod.Id) {
          dispatch(addPaymentMethodToStore({
            id: newMethod.Id,
            value: methodName.trim().toUpperCase().replace(/\s+/g, '_'),
            label: methodName.trim(),
            name: methodName.trim(),
            isActive: true
          }));
        }
        
        toast.success(t('payment_method_created') || 'Payment method created successfully');
      }

      await fetchPaymentMethods();
      handleClose();
    } catch (error) {
      console.error('Error saving payment method:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(errorMessage || (editMethod ? t('failed_to_update') : t('failed_to_create')));
    }
  };

  const handleDelete = async () => {
    if (!methodToDelete) return;

    try {
      await paymentMethodService.deletePaymentMethod(methodToDelete.Id);
      
      // Remove from Redux store
      dispatch(removePaymentMethodFromStore(methodToDelete.Id));
      
      toast.success(t('payment_method_deleted') || 'Payment method deleted successfully');
      await fetchPaymentMethods();
      setDeleteConfirmOpen(false);
      setMethodToDelete(null);
    } catch (error) {
      console.error('Error deleting payment method:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(errorMessage || t('failed_to_delete') || 'Failed to delete payment method');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditMethod(null);
    setMethodName('');
    setDescription('');
    setError('');
  };

  const handleOpenAdd = () => {
    setOpen(true);
    setEditMethod(null);
    setMethodName('');
    setDescription('');
    setError('');
  };

  const handleOpenEdit = (method) => {
    setEditMethod(method);
    setMethodName(method.Name);
    setDescription(method.Description || '');
    setError('');
    setOpen(true);
  };

  const handleOpenDeleteConfirm = (method) => {
    setMethodToDelete(method);
    setDeleteConfirmOpen(true);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Payment sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h5" fontWeight="600">
            {t('payment_methods') || 'Payment Methods'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenAdd}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          {t('add_payment_method') || 'Add Payment Method'}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : paymentMethods.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px' }}>
          <Payment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('no_payment_methods') || 'No payment methods found'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('add_first_payment_method') || 'Add your first payment method to get started'}
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
            {t('add_payment_method') || 'Add Payment Method'}
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t('id') || 'ID'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t('payment_method_name') || 'Payment Method Name'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t('description') || 'Description'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t('status') || 'Status'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t('actions') || 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentMethods.map((method) => (
                <TableRow
                  key={method.Id}
                  sx={{
                    '&:hover': {
                      bgcolor: 'grey.50',
                    },
                  }}
                >
                  <TableCell>{method.Id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="500">
                      {method.Name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {method.Description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={method.IsActive ? t('active') || 'Active' : t('inactive') || 'Inactive'}
                      size="small"
                      color={method.IsActive ? 'success' : 'default'}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenEdit(method)}
                      sx={{
                        '&:hover': {
                          bgcolor: 'primary.lighter',
                        },
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteConfirm(method)}
                      sx={{
                        '&:hover': {
                          bgcolor: 'error.lighter',
                        },
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
          {editMethod ? t('edit_payment_method') || 'Edit Payment Method' : t('add_payment_method') || 'Add Payment Method'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              label={t('payment_method_name') || 'Payment Method Name'}
              value={methodName}
              onChange={(e) => {
                setMethodName(e.target.value);
                setError('');
              }}
              fullWidth
              autoFocus
              required
              placeholder={t('enter_payment_method_name') || 'e.g., Cash, Bancontact, Credit Card'}
              inputProps={{
                maxLength: 100,
              }}
              helperText={`${methodName.length}/100 ${t('characters') || 'characters'}`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
                mb: 2,
              }}
            />
            <TextField
              label={t('description') || 'Description'}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setError('');
              }}
              fullWidth
              multiline
              rows={3}
              placeholder={t('enter_description') || 'Optional description for this payment method'}
              inputProps={{
                maxLength: 500,
              }}
              helperText={`${description.length}/500 ${t('characters') || 'characters'}`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
            }}
          >
            {t('save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('confirm_delete') || 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('delete_payment_method_confirm') || 'Are you sure you want to delete'} <strong>{methodToDelete?.Name}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('delete_warning') || 'This action cannot be undone.'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
            }}
          >
            {t('delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMethod;
